/**
 * Upload hand-off.
 *
 * Lets a shopper pick their artwork anywhere on the site (the "UPLOAD" step on the
 * home page, for instance) and have it uploaded on the custom rug page, as if they
 * had picked it there. A File cannot be carried in a URL, so it is parked in
 * IndexedDB, the browser navigates, and the custom rug page replays it through the
 * normal upload flow.
 *
 * Markup side: any element with data-upload-handoff="<destination url>" becomes the
 * picker. It is clicked, the shopper chooses a file, and the destination is opened.
 *
 * Script side:
 *   UploadHandoff.save(file);   // park a file for the next page
 *   UploadHandoff.isPending();  // synchronous: is one waiting for us?
 *   UploadHandoff.take();       // read it and remove it (once)
 *   UploadHandoff.clear();
 */
(function () {
  if (window.UploadHandoff) return;

  const DB_NAME = 'rugtomize';
  const DB_VERSION = 1;
  const STORE = 'upload-handoff';
  const KEY = 'pending';
  // Read synchronously on the receiving page, so it can show a loader before the
  // (asynchronous) IndexedDB read tells it whether there is anything to load.
  const FLAG = 'rugtomize:upload-handoff';
  // A file parked and never collected goes stale rather than surfacing on some
  // unrelated visit later in the session.
  const MAX_AGE_MS = 30 * 60 * 1000;
  // Mirrors UploadButton.MAX_SUPPORTED_FILE_SIZE, so an oversized file is rejected
  // here instead of after a pointless navigation.
  const MAX_FILE_SIZE = 250 * 1024 * 1024;
  // Up to this size the bytes are copied into the record (see save). Past it the File
  // is parked by reference instead: reading that much into memory is the bigger risk on
  // a phone, and a file that large comes from a desktop, where the reference survives.
  const MAX_INLINE_BYTES = 100 * 1024 * 1024;

  /* ---- STORAGE ---- */

  function openDb() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) return reject(new Error('IndexedDB unavailable'));

      let request;
      try {
        request = indexedDB.open(DB_NAME, DB_VERSION);
      } catch (error) {
        return reject(error);
      }

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('IndexedDB blocked'));
    });
  }

  function runTransaction(db, mode, work) {
    return new Promise((resolve, reject) => {
      let transaction;
      try {
        transaction = db.transaction(STORE, mode);
      } catch (error) {
        return reject(error);
      }

      const request = work(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve(request?.result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  function setFlag() {
    try {
      sessionStorage.setItem(FLAG, String(Date.now()));
    } catch (error) {
      // Private mode or a full quota. The file is stored either way; only the
      // fast "is one waiting?" check is lost, and take() still finds it.
    }
  }

  function clearFlag() {
    try {
      sessionStorage.removeItem(FLAG);
    } catch (error) {
      /* nothing to clear */
    }
  }

  function isPending() {
    let raw;
    try {
      raw = sessionStorage.getItem(FLAG);
    } catch (error) {
      return false;
    }
    if (!raw) return false;

    if (Date.now() - Number(raw) > MAX_AGE_MS) {
      clearFlag();
      clear();
      return false;
    }

    return true;
  }

  function readBytes(file) {
    if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  // The bytes are copied into the record rather than the File being stored as it is.
  // WebKit keeps a File put into IndexedDB as a reference to its backing file, and the
  // file a picker hands out lives in a temporary location that iOS tears down along with
  // the page that opened the picker. Read back on the product page, that File is there
  // but empty, so the upload sends nothing and the canvas waits on an image that never
  // arrives. A copy owes nothing to the original.
  async function save(file) {
    const record = { name: file.name, type: file.type, savedAt: Date.now() };

    if (file.size <= MAX_INLINE_BYTES) record.bytes = await readBytes(file);
    else record.file = file;

    const db = await openDb();
    try {
      await runTransaction(db, 'readwrite', (store) => store.put(record, KEY));
    } finally {
      db.close();
    }
    setFlag();
  }

  async function take() {
    clearFlag();

    let db;
    try {
      db = await openDb();
    } catch (error) {
      return null;
    }

    try {
      const record = await runTransaction(db, 'readonly', (store) => store.get(KEY));
      // Removed whether or not it is usable: a hand-off is collected once.
      await runTransaction(db, 'readwrite', (store) => store.delete(KEY));

      if (!record) return null;
      if (Date.now() - (record.savedAt || 0) > MAX_AGE_MS) return null;

      const name = record.name || 'design';
      const type = record.type || '';

      // Rebuilt so the receiving page always gets a File with a name, whether the record
      // carries the copied bytes or — over MAX_INLINE_BYTES — the File itself.
      const file = record.bytes
        ? new File([record.bytes], name, { type: type })
        : record.file instanceof File
          ? record.file
          : record.file
            ? new File([record.file], name, { type: type || record.file.type || '' })
            : null;

      // A File that reads as empty is what a lost backing file looks like. Handing it on
      // would open the builder on a design that can never load, so it counts as nothing
      // parked and the shopper gets the ordinary upload button instead.
      if (!file || file.size === 0) return null;

      return file;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      db.close();
    }
  }

  async function clear() {
    clearFlag();
    let db;
    try {
      db = await openDb();
    } catch (error) {
      return;
    }
    try {
      await runTransaction(db, 'readwrite', (store) => store.delete(KEY));
    } catch (error) {
      /* already gone */
    } finally {
      db.close();
    }
  }

  /* ---- TRIGGERS ---- */

  let picker = null;
  let activeTrigger = null;

  function pickerElement() {
    if (picker) return picker;

    picker = document.createElement('input');
    picker.type = 'file';
    picker.style.display = 'none';
    // No accept list: every file type is accepted, exactly as on the product page.
    picker.addEventListener('change', onPickerChange);
    document.body.appendChild(picker);

    return picker;
  }

  function triggerFrom(target) {
    return target?.closest?.('[data-upload-handoff]') || null;
  }

  function open(trigger) {
    if (!trigger || trigger.dataset.busy === 'true') return;
    if (!trigger.getAttribute('data-upload-handoff')) return;

    activeTrigger = trigger;
    window.theme?.utils?.track?.('Upload CTA', {
      cta_location: trigger.getAttribute('data-upload-handoff-location') || 'handoff'
    });
    pickerElement().click();
  }

  function onDocumentClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;

    const trigger = triggerFrom(event.target);
    if (!trigger) return;
    // A real link or button inside the card keeps its own behaviour.
    if (event.target.closest('a[href], button, input, select, textarea')) return;

    event.preventDefault();
    open(trigger);
  }

  function onDocumentKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

    const trigger = triggerFrom(event.target);
    if (!trigger || trigger !== event.target) return;

    event.preventDefault();
    open(trigger);
  }

  async function onPickerChange(event) {
    const input = event.target;
    const file = input.files?.[0];
    const trigger = activeTrigger;
    input.value = '';
    activeTrigger = null;

    if (!file || !trigger) return;

    const destination = trigger.getAttribute('data-upload-handoff');

    if (file.size > MAX_FILE_SIZE) {
      window.theme?.utils?.track?.('Upload Failed', {
        file_type: file.type || '',
        file_size_kb: Math.round(file.size / 1024),
        error_stage: 'file_too_large'
      });
      alert('Maximum supported file size is 250MB.');
      return;
    }

    trigger.dataset.busy = 'true';
    window.GlobalLoader?.show(['Getting your design ready...', 'Opening the rug designer...']);

    try {
      await save(file);
      window.theme?.utils?.track?.('Upload Handoff', {
        file_type: file.type || '',
        file_size_kb: Math.round(file.size / 1024),
        cta_location: trigger.getAttribute('data-upload-handoff-location') || 'handoff'
      });
    } catch (error) {
      // The file could not be parked (no IndexedDB, no quota). The shopper still
      // wanted the designer, so take them there; they can upload on the page itself.
      console.error(error);
      window.theme?.utils?.track?.('Upload Handoff Failed', { error_stage: 'store' });
      window.GlobalLoader?.hide();
      trigger.dataset.busy = 'false';
    }

    if (destination) window.location.href = destination;
  }

  // Coming back from the product page restores this one as it was left, loader and all.
  function onPageShow(event) {
    if (!event.persisted) return;
    window.GlobalLoader?.hide();
    document.querySelectorAll('[data-upload-handoff][data-busy="true"]').forEach((trigger) => {
      trigger.dataset.busy = 'false';
    });
  }

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeyDown);
  window.addEventListener('pageshow', onPageShow);

  window.UploadHandoff = {
    save: save,
    take: take,
    clear: clear,
    isPending: isPending,
    MAX_FILE_SIZE: MAX_FILE_SIZE
  };
})();
